import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentMethodCard = ({ method, isSelected, onToggle }) => {
  const getMethodIcon = (methodType) => {
    const icons = {
      'VA Transfer': 'Building2',
      'QRIS': 'QrCode',
      'OVO': 'Wallet',
      'DANA': 'Wallet',
      'GoPay': 'Wallet',
      'Card': 'CreditCard'
    };
    return icons?.[methodType] || 'DollarSign';
  };

  const getMethodColor = (methodType) => {
    const colors = {
      'VA Transfer': 'bg-blue-500',
      'QRIS': 'bg-purple-500',
      'OVO': 'bg-indigo-500',
      'DANA': 'bg-cyan-500',
      'GoPay': 'bg-green-500',
      'Card': 'bg-orange-500'
    };
    return colors?.[methodType] || 'bg-gray-500';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return 'TrendingUp';
    if (trend < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'text-success';
    if (trend < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  return (
    <div
      onClick={onToggle}
      className={`card cursor-pointer transition-all duration-250 ${
        isSelected
          ? 'ring-2 ring-primary shadow-elevation-3'
          : 'hover:shadow-elevation-2'
      }`}
    >
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3 md:gap-4">
          <div
            className={`w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-xl ${getMethodColor(
              method?.name
            )} flex items-center justify-center`}
          >
            <Icon
              name={getMethodIcon(method?.name)}
              size={20}
              color="white"
              className="md:w-6 md:h-6 lg:w-7 lg:h-7"
            />
          </div>
          <div>
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">
              {method?.name}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-0.5 md:mt-1">
              {method?.category}
            </p>
          </div>
        </div>
        <div
          className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all duration-250 ${
            isSelected
              ? 'border-primary bg-primary' :'border-border bg-background'
          }`}
        >
          {isSelected && <Icon name="Check" size={14} color="white" />}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:gap-6">
        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
            Success Rate
          </p>
          <div className="flex items-center gap-2">
            <p className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              {method?.successRate}%
            </p>
            <div
              className={`flex items-center gap-1 ${getTrendColor(
                method?.successTrend
              )}`}
            >
              <Icon name={getTrendIcon(method?.successTrend)} size={14} />
              <span className="text-xs md:text-sm font-medium">
                {Math.abs(method?.successTrend)}%
              </span>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
            Avg Transaction
          </p>
          <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
            {formatCurrency(method?.avgTransaction)}
          </p>
        </div>

        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
            Total Transactions
          </p>
          <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
            {method?.totalTransactions?.toLocaleString('id-ID')}
          </p>
        </div>

        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
            Adoption Rate
          </p>
          <div className="flex items-center gap-2">
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">
              {method?.adoptionRate}%
            </p>
            <div
              className={`flex items-center gap-1 ${getTrendColor(
                method?.adoptionTrend
              )}`}
            >
              <Icon name={getTrendIcon(method?.adoptionTrend)} size={12} />
              <span className="text-xs font-medium">
                {Math.abs(method?.adoptionTrend)}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex items-center justify-between text-xs md:text-sm">
          <span className="text-muted-foreground">Revenue Contribution</span>
          <span className="font-semibold text-foreground">
            {method?.revenueContribution}%
          </span>
        </div>
        <div className="mt-2 md:mt-3 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full ${getMethodColor(method?.name)} transition-all duration-500`}
            style={{ width: `${method?.revenueContribution}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodCard;